/**
 * section-brain.tsx — Mục "Bộ não" của màn nhân viên AI (Cần phát hành).
 *   - AI tích hợp sẵn: mặc định THEO MODEL CỦA TỔ CHỨC (provider/model NULL — đổi ở Nguồn AI và lượt AI, mở tab mới +
 *     "Tải lại"). Công tắc "Dùng model khác cho nhân viên này" → lưới thẻ model dùng chung (`ModelCardGrid`) theo nguồn của
 *     tổ chức (`GET /ai/models`); model khoá theo gói không chọn được; Key của tôi mà nhà cung cấp chưa có key hợp lệ → cảnh
 *     báo + link thêm key (tab mới) + Tải lại. Tắt công tắc → gửi provider:null, model:null (trở về theo tổ chức).
 *   - AI bên thứ ba / Webhook: nền tảng đang kết nối + nút "Sửa thông tin kết nối" (form kết nối `ai-member-form-dialog.tsx`).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { ReloadButton } from '@/components/ai/reload-button'
import { ModelCardGrid } from '@/components/ai/model-card-grid'
import { inheritsOrgModel, keyTag, lockLabel, orderModels, providerLabel, type ModelRef } from '@/components/ai/model-card-grid-lib'
import { LegacyProviderWarning } from '@/components/shared/ai-provider-option'
import { aiKeys, useAiConfig } from '@/hooks/use-ai'
import { aiCreditKeys, useAiModels, useAiSources } from '@/hooks/use-ai-credits'
import { AlertTriangle, Brain, Building2, Loader2, PlugZap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import { findModel, perReplyText, SOURCE_LABEL } from '@/lib/ai-credits'
import { isLegacyAiProvider } from '@/lib/ai-providers'
import { AI_PATHS } from '@/lib/ai-khung'
import { TRAIN_BOT_RELOAD_EVENT } from '@/hooks/use-ai-train-test'
import { AiMemberFormDialog } from '@/pages/team/ai-member-form-dialog'
import { KIND_BADGE, platformOf } from '@/pages/team/ai-member-form-lib'
import type { SectionProps } from '../train/draft-form'
import type { TrainBot } from '../train/train-api'
import { orgSourceMode } from '../sources/source-mode-lib'
import { StaffPanel } from './staff-ui'

export function BrainSection({ form, set, canEdit, bot }: SectionProps & { bot: TrainBot }) {
  // Đổi Nguồn AI (PUT /ai/sources) cùng quyền `ai.update` với sửa nhân viên AI → dùng chung `canEdit` (đã theo quyền).
  const { t } = useTranslation('aiStaff')
  const canSources = canEdit
  const [connOpen, setConnOpen] = useState(false)

  if (bot.kind !== 'builtin') {
    const label = bot.kind === 'webhook' ? t('brain.external.webhookLabel') : platformOf(bot.kind)?.label ?? KIND_BADGE[bot.kind]
    return (
      <StaffPanel
        id="staff-bo-nao" title={t('brain.title')} icon={<Brain className="h-4 w-4" />}
        description={t('brain.external.description')}
              >
        <div className="flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3">
          <PlugZap className="h-4 w-4 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{label}</p>
            <p className="text-xs text-muted-foreground">
              {bot.kind === 'webhook' ? t('brain.external.webhookHint') : t('brain.external.platformHint')}
            </p>
          </div>
          {bot.externalConfigBroken && <Badge variant="destructive">{t('brain.external.configBroken')}</Badge>}
          {canEdit && <Button size="sm" variant="outline" onClick={() => setConnOpen(true)}>{t('brain.external.editConnection')}</Button>}
        </div>
        <AiMemberFormDialog open={connOpen} mode={bot.kind === 'webhook' ? 'webhook' : 'third_party'} bot={bot} canEdit={canEdit}
          onClose={() => { setConnOpen(false); window.dispatchEvent(new CustomEvent(TRAIN_BOT_RELOAD_EVENT, { detail: { botId: bot.id } })) }} />
      </StaffPanel>
    )
  }

  return <BuiltinBrain form={form} set={set} canEdit={canEdit} canSources={canSources} />
}

const RELOAD_KEYS = [aiCreditKeys.models, aiCreditKeys.sources, aiCreditKeys.credits, aiKeys.config]

function BuiltinBrain({ form, set, canEdit, canSources }: Pick<SectionProps, 'form' | 'set' | 'canEdit'> & { canSources: boolean }) {
  const { t } = useTranslation('aiStaff')
  const { data: config } = useAiConfig()
  const { data: modelsData, isLoading } = useAiModels()
  const { data: sources } = useAiSources()
  const own: ModelRef = { provider: form.provider, model: form.model }
  const inherited = inheritsOrgModel(own)
  // Bật công tắc nhưng chưa bấm thẻ nào: chưa lưu gì (bot vẫn theo tổ chức) — chỉ mở lưới.
  const [pickOpen, setPickOpen] = useState(false)
  const customOn = !inherited || pickOpen

  const models = orderModels(modelsData?.models)
  const names = sources?.providers.map((p) => ({ provider: p.provider, name: p.name })) ?? null
  const byokOf = (provider: string) => sources?.providers.find((p) => p.provider === provider)?.byok
  const orgModel = config ? findModel(models, config.provider, config.model) : undefined
  const orgSource = config ? sources?.providers.find((p) => p.provider === config.provider)?.source : undefined
  const mode = orgSourceMode(sources?.providers)
  const ownEntry = findModel(models, form.provider, form.model)
  const ownKey = ownEntry?.source === 'byok' ? keyTag(byokOf(ownEntry.provider)) : null
  const reload = <ReloadButton queryKeys={RELOAD_KEYS} what={t('brain.reloadWhat')} iconOnly />

  function toggle(on: boolean) {
    if (on) { setPickOpen(true); return }
    setPickOpen(false)
    if (!inherited) set({ provider: null, model: null })
  }

  return (
    <StaffPanel
      id="staff-bo-nao" title={t('brain.title')} icon={<Brain className="h-4 w-4" />} scope="publish"
      description={t('brain.builtin.description')}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2.5 text-sm">
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            {inherited ? t('brain.builtin.usingOrgModel') : t('brain.builtin.orgModel')}
            <b>{config ? orgModel?.title ?? config.model : '…'}</b>
            {orgSource ? <span className="text-muted-foreground"> · {SOURCE_LABEL[orgSource]}</span> : null}
            {orgModel?.source === 'platform' && perReplyText(orgModel) ? <span className="text-muted-foreground"> · {perReplyText(orgModel)}</span> : null}
          </span>
          {canSources && <OpenInNewTab to={AI_PATHS.sources} className="text-xs" title={t('brain.builtin.openSourcesTitle')}>{t('brain.builtin.changeInSources')}</OpenInNewTab>}
          {reload}
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="bot-own-model" className="text-sm font-medium">{t('brain.builtin.useOtherModel')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('brain.builtin.pickHint', { source: mode === 'platform' || mode === 'byok' ? SOURCE_LABEL[mode] : t('brain.builtin.orgSource') })}{' '}
              {t('brain.builtin.pickOff')}
            </p>
          </div>
          <Switch id="bot-own-model" checked={customOn} disabled={!canEdit} onCheckedChange={toggle} />
        </div>

        {customOn && (
          <div className="space-y-3">
            {isLegacyAiProvider(form.provider) && form.provider && <LegacyProviderWarning provider={form.provider} />}
            {!inherited && form.model && !ownEntry && !isLoading && (
              <p className="text-xs text-warning">{t('brain.builtin.modelGone', { model: form.model })}</p>
            )}
            {inherited && <p className="text-xs text-muted-foreground">{t('brain.builtin.clickModel')}</p>}
            {isLoading ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('brain.builtin.loadingModels')}</p>
            ) : models.length === 0 ? (
              <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">{t('brain.builtin.noModels')}</p>
            ) : (
              <ModelCardGrid
                models={models} compact current={inherited ? null : own} selected={inherited ? null : own}
                byokOf={byokOf} names={names} disabled={!canEdit} ariaLabel={t('brain.builtin.gridAria')} currentLabel={t('brain.builtin.currentLabel')}
                onSelect={(m) => {
                  const lock = lockLabel(m)
                  if (lock) { toast.error(t('brain.builtin.lockedToast', { title: m.title, lock })); return }
                  setPickOpen(false)
                  set({ provider: m.provider, model: m.model })
                }}
              />
            )}
            {ownEntry && ownKey && ownKey.label !== 'Có key' && ( // i18n-ignore: so sánh định danh KeyTagLabel, không phải chữ hiển thị
              <div role="alert" className="flex flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
                <span className="min-w-0 flex-1">
                  {ownKey.label === 'Key lỗi' ? t('brain.builtin.keyBroken', { provider: providerLabel(ownEntry.provider, names) }) : t('brain.builtin.keyMissing', { provider: providerLabel(ownEntry.provider, names) })}{/* i18n-ignore: so sánh định danh KeyTagLabel */}
                  {' '}{t('brain.builtin.keyNoReply', { model: ownEntry.title })}
                </span>
                {canSources && <OpenInNewTab to={AI_PATHS.sources} className="text-xs" title={t('brain.builtin.openSourcesTitle')}>{t('brain.builtin.addKey')}</OpenInNewTab>}
                {reload}
              </div>
            )}
          </div>
        )}
      </div>
    </StaffPanel>
  )
}
